import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  ConvertTo3waClient,
  ConvertTo3waOptions,
  LocationJsonResponse,
  LocationGeoJsonResponse,
  AutosuggestOptions,
  AutosuggestResponse,
  AutosuggestClient
} from '@what3words/api';
import { ParcelCapturePayload, WaybillCompletePayload, WaybillPayload,PrintDeliveryLabelPayload,WaybillOrderApiResponse } from 'src/interfaces/waybill';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from './auth.service';
import { ConfirmationService } from './confirmation.service';
import { AlertService } from './alert.service';
import { Order, TrackingDetails } from 'src/interfaces/order';
import { OrderTrackDialogComponent } from '../components/order-track-dialog/order-track-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UtilsService } from './utils.service';
import { ShippingFormComponent } from '../components/shipping-form/shipping-form.component';
import { PrescriptionFormComponent} from '../components/prescription-form/prescription-form.component';

const testWaybillNumbers: string[] = [
  'W1',
  'W2',
  'W3',
  'W4',
  'W5',
  'waybill-test1',
  'waybill-test2',
  'waybill-test3',
  'waybill-test4',
  'waybill-test5',
  'waybill-test6'
];


export interface WaybillResponse {
  success: boolean;
  data: any;
}


// code continues...
@Injectable({
  providedIn: 'root'
})
export class ShippingService {

  API_KEY = environment.what3words.apiKey;
  dawningApiBaseUrl = environment.waybill.baseUrl;
  baseUrl: string = environment.api.baseUrl;
  userName = environment.waybill.userName;
  password = environment.waybill.password;
  dawngWingheaders: HttpHeaders;
  headers: HttpHeaders;
  printServerBaseUrl: string = environment.api.printServerBaseUrl;
  
  constructor(
    private _http: HttpClient,
    private _auth: AuthService,
    private _confirmationService: ConfirmationService,
    private _alertService: AlertService,
    private _dialog: MatDialog,
    private _utilsService: UtilsService
  ) {
    (async () => {
      this.headers = await this._auth.generateRequestHeaders();
      this.dawngWingheaders = await this.getRequestHeaders();
    })();
  }



  async getWhat3WordsAddress(options: ConvertTo3waOptions): Promise<LocationJsonResponse | LocationGeoJsonResponse> {
    const client: ConvertTo3waClient = ConvertTo3waClient.init(this.API_KEY);
    return await client.run(options);
  }

  async getWhat3WordsAutosuggest(options: AutosuggestOptions): Promise<AutosuggestResponse> {
    const client: AutosuggestClient = AutosuggestClient.init(this.API_KEY);
    return await client.run(options);
  }

  /**
   * POST /waybill/auth — `data` may be empty/null while `success: true`; that is valid (no Dawning token yet).
   * Returns `''` silently; only HTTP failures and non-empty invalid JSON surface errors.
   */
  async getToken(): Promise<string> {
    try {
      const res = await this._http
        .post<{ data?: unknown }>(`${this.baseUrl}/waybill/auth`, {
          userName: this.userName,
          password: this.password,
        })
        .toPromise();

      const data = res?.data;
      // Empty / null payload is OK — no Bearer token for external waybill API until backend supplies one.
      if (data == null || data === '') {
        return '';
      }

      let payload: { token?: string };
      if (typeof data === 'string') {
        const s = data.trim();
        if (!s) {
          return '';
        }
        try {
          payload = JSON.parse(s) as { token?: string };
        } catch (e) {
          const msg = `Waybill auth data is not valid JSON: ${(e as Error).message}`;
          console.warn('[ShippingService.getToken]', msg, { raw: s });
          this._alertService.errorAlert(msg);
          return '';
        }
      } else if (typeof data === 'object') {
        const d = data as Record<string, unknown>;
        payload =
          typeof d.token === 'string'
            ? (d as { token?: string })
            : d.data != null && typeof d.data === 'object'
            ? (d.data as { token?: string })
            : (d as { token?: string });
      } else {
        return '';
      }

      return typeof payload?.token === 'string' ? payload.token : '';
    } catch (error) {
      this._utilsService.showError(error);
      return '';
    }
  }

  async getRequestHeaders(): Promise<HttpHeaders> {

    let token = await this.getToken();
    let headers = new HttpHeaders();
    headers = headers.append("Authorization", `Bearer ${token}`);
    return headers;
  }

  async captureWayBill(waybillPayload: WaybillPayload | null): Promise<WaybillResponse> {

    const confirm = await this._confirmationService
      .confirm("Capture your waybill",
        "Please make sure you have entered the correct information. Click 'Yes' to proceed.");

    if (!confirm) return {
      success: false,
      data: null
    };

    try {
      const { success, data } = await this
        ._http
        .post<any>(`${this.baseUrl}/waybill/capture`,
          waybillPayload,
          {
            headers: this.dawngWingheaders
          })
        .toPromise();

      let links: any = data.data ? data.data : null;

      return {
        success,
        data: links
      };

    } catch (error) {
      this._utilsService.showError(error);
      return {
        success: false,
        data: null
      };
    }
  }

  async captureParcel(payload: ParcelCapturePayload): Promise<boolean> {


    const confirm = await this._confirmationService
      .confirm("Capture your parcel",
        "Please make sure you have entered the correct information. Click 'Yes' to proceed.");

    if (!confirm) return false;

    try {
      const { success } = await this
        ._http
        .post<any>(`${this.baseUrl}/parcel/capture`,
          payload,
          {
            headers: this.dawngWingheaders
          })
        .toPromise();
      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }


  async completeWaybill(payload: WaybillCompletePayload): Promise<boolean> {


    const confirm = await this._confirmationService
      .confirm("Complete your waybill",
        "Once a waybill has been completed it cannot be changed. No new parcels can be captured. Click 'Yes' to proceed.");

    if (!confirm) return false;

    try {
      const { success } = await this
        ._http
        .post<any>(`${this.baseUrl}/waybill/complete`,
          payload,
          {
            headers: this.dawngWingheaders
          })
        .toPromise();
      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  async markOrderAsShipped(id: number): Promise<boolean> {
    const confirmed = await this._confirmationService
      .confirm("Confirm shipment", "Please check if this order has been shipped.");

    if (!confirmed) return false;

    try {

      const { success, message } = await this._http
        .get<any>(`${this.baseUrl}/order/${id}/shipped`, {
          headers: this.headers
        })
        .toPromise();

      this._alertService
        .openSnackBar(message, "close");

      return success;
    } catch (error: any) {
      this._alertService.errorAlert(error.error.message);
      return false;
    }
  }


  generateWaybillNumber(): Promise<any> {
    return this._http
        .get<any>(`${this.baseUrl}/waybill/generate`, {
          headers: this.headers
        })
        .toPromise();
  }

  async trackOrder(orderId: number): Promise<TrackingDetails[]> {
    try {
      const response = await this._http
        .get<any>(`assets/tracking.json`, {
          headers: this.headers
        })
        .toPromise();
      return response;
    } catch (error) {
      this._utilsService
        .showError(error);
      return [];
    }
  }

  async openOrderTrackDialog(
    order: Order,
    title: string = "Track your order",
    message: string = ""): Promise<boolean> {

    const trackingDetails: TrackingDetails[] = await this.trackOrder(order.id);

    const dialogRef = this._dialog.open(OrderTrackDialogComponent, {
      width: '800px',
      data: {
        title,
        message,
        order
      }
    });

    return dialogRef.afterClosed().toPromise();
  }

  async openShippingFormDialog(
    order: Order,
    title: string = "Capture waybill",
    message: string = ""): Promise<boolean> {

    const dialogRef = this._dialog.open(ShippingFormComponent, {
      width: '900px',
      maxWidth: '95vw',
      panelClass: ['mbp-confirm-dialog', 'mbp-waybill-dialog'],
      data: {
        title,
        message,
        order
      }
    });

    return dialogRef.afterClosed().toPromise();
  }

async openStatusPackingDialog(
    order: Order,
    title: string = "Capture waybill",
    message: string = ""): Promise<boolean> {

    // Check if the order's status is "Packaging"
    if (order.status.status !== 'Packaging') {
        console.warn('This order is not in the Packaging status');
        return Promise.resolve(false);  // Return early if the order is not in "Packaging" status
    }

    const dialogRef = this._dialog.open(PrescriptionFormComponent, {
        width: '1200px',
        panelClass: 'shipping-dialog',
        data: {
            title,
            message,
            order
        }
    });

    return dialogRef.afterClosed().toPromise();
}


  async openEditOrderDialog(
    order: Order,
    title: string = 'Capture waybill',
    message: string = ''
  ): Promise<boolean> {

    const dialogRef = this._dialog.open(PrescriptionFormComponent, {
      width: '1200px',
      panelClass: 'shipping-dialog',
      data: {
        title,
        message,
        order,
      },
    });

    return dialogRef.afterClosed().toPromise();
  }

  async printDeliveryLabel(payload: PrintDeliveryLabelPayload): Promise<boolean> {

    try {

      const { message } = await this
        ._http
        .post<any>(
          `${this.printServerBaseUrl}/deliverylabel/`, payload)
        .toPromise();

      this._alertService.infoAlert(message);

      return true;

    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }
  async getWayBillOrders(waybillNumber:string): Promise<WaybillOrderApiResponse> {
      try {

      const  data  = await this._http
        .get<any>(`${this.baseUrl}/waybillorder/${waybillNumber}`, {
          headers: this.headers
        })
        .toPromise();


      return data;
    } catch (error: any) {
      this._alertService.errorAlert(error.error.message);
      return error;
    }
  }


}
